import styled from "@emotion/styled/macro";
import { tokens, Typography } from "@sunrun/experience-ui-components";
import { PersistentTooltip } from "checkout/components/atoms/PersistentTooltip";
import { type PromotionSelectionItem } from "services/promotions";

type OfferReviewPromotionPillsProps = {
  promotions: PromotionSelectionItem[];
};

const OfferReviewPromotionPills = ({
  promotions,
}: OfferReviewPromotionPillsProps) => {
  if (!promotions.length) {
    return null;
  }

  return (
    <Container data-testid="offer-review-promotion-pills">
      {promotions.map((promotion) => (
        <PromotionPill key={promotion.id}>
          <PromotionName>{promotion.promoName}</PromotionName>
          <PersistentTooltip
            direction="bottom-left"
            trigger={
              <DisclaimerTriggerText>View disclaimers</DisclaimerTriggerText>
            }
          >
            <TooltipContent>
              <Typography size={16} color={tokens.INFO_30}>
                This offer meets the initial criteria for the listed promotion.
                Additional qualification criteria may apply.
                {promotion.stackable === "No" &&
                  " Promotion not stackable with other promotions."}
              </Typography>
              {promotion.termsAndConditionsLink && (
                <TermsLink
                  href={promotion.termsAndConditionsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and conditions
                </TermsLink>
              )}
            </TooltipContent>
          </PersistentTooltip>
        </PromotionPill>
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 20px 0;
`;

const PromotionPill = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${tokens.SUCCESS_70};
  background-color: ${tokens.SUCCESS_90};
`;

const PromotionName = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.32px;
  color: ${tokens.SUCCESS_20};
  word-break: break-word;
`;

const DisclaimerTriggerText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -0.28px;
  color: ${tokens.BRAND_HERO_BLUE};
  text-decoration: underline;
  text-underline-position: from-font;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TermsLink = styled.a`
  color: ${tokens.INFO_30};
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${tokens.INFO_40};
  }
`;

export { OfferReviewPromotionPills };
